import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, exhaustMap, from, map, of, tap } from 'rxjs';
import { firstFromEnvelope } from '../../envelope';
import { Api as AdministrationApi } from '../../api/generated/administration/api';
import { getUserProfile } from '../../api/generated/administration/fn/users/get-user-profile';
import { AppError } from '../../models/app-error.model';
import { OfflineDb } from '../../offline/db';
import { AuthActions } from '../auth/auth.actions';
import { UserActions } from './user.actions';

function errorMessage(error: unknown): string {
  const message = (error as AppError)?.message;
  return typeof message === 'string' && message.trim() ? message : 'Failed to load profile';
}

@Injectable()
export class UserEffects {
  private readonly actions$ = inject(Actions);
  private readonly administrationApi = inject(AdministrationApi);
  private readonly db = inject(OfflineDb);

  readonly loadProfile$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.loadProfileRequested),
      exhaustMap(() =>
        this.administrationApi.invoke(getUserProfile, {}).pipe(
          map((body) => firstFromEnvelope(body.data)),
          exhaustMap((profile) => {
            if (!profile) {
              return of(UserActions.loadProfileFailed({ error: 'Profile unavailable' }));
            }
            const syncedAt = new Date().toISOString();
            return from(
              (async () => {
                await this.db.userProfile.clear();
                await this.db.userProfile.put({ id: 'current', profile, syncedAt });
                return UserActions.loadProfileSucceeded({ profile, syncedAt });
              })(),
            );
          }),
          catchError((error: unknown) =>
            of(UserActions.loadProfileFailed({ error: errorMessage(error) })),
          ),
        ),
      ),
    ),
  );

  readonly clearCachedProfile$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UserActions.reset, AuthActions.sessionCleared, AuthActions.refreshFailed),
        tap(() => void this.db.userProfile.clear()),
      ),
    { dispatch: false },
  );
}
